// tracking-cart.js
// Central fetch interceptor for all cart events.
// Fires: view_cart, add_to_cart, remove_from_cart
// Internal events only — no GA4 or Meta calls are made here.
//
// Exposes window.__rainbowCartItems (raw Shopify cart items array, kept fresh)
// for use by tracking-begin-checkout.liquid without an extra network request.

(function () {
  if (window.__rainbowCartTrackingPatched) return;
  window.__rainbowCartTrackingPatched = true;

  // Public cart cache — updated after every successful cart API response
  window.__rainbowCartItems = window.__rainbowCartItems || [];

  // Debounce view_cart: do not fire more than once per 3 seconds
  var _lastViewCart = 0;

  function _updateCache(cartData) {
    if (cartData && Array.isArray(cartData.items)) {
      window.__rainbowCartItems = cartData.items;
    }
  }

  function _getCurrency() {
    return window.getCurrency ? window.getCurrency() : 'EUR';
  }

  function _safeTrack(eventData) {
    if (!window.trackEvent) return;
    try { window.trackEvent(eventData); } catch (e) { console.warn('Tracking error:', e); }
  }

  function _urlPath(url) {
    if (typeof url !== 'string') return '';
    var path = url.split('?')[0];
    var idx = path.indexOf('/cart');
    return idx !== -1 ? path.substring(idx) : path;
  }

  function _mapItem(item, overrideQty) {
    var qty = overrideQty !== undefined ? overrideQty : item.quantity;
    var mapped = {
      item_id: item.handle || String(item.product_id),
      item_name: item.product_title,
      price: parseFloat(item.price) / 100,
      quantity: qty
    };
    if (item.vendor) mapped.item_brand = item.vendor;
    if (item.product_type) mapped.item_category = item.product_type;
    if (item.variant_title && item.variant_title !== 'Default Title') {
      mapped.item_variant = item.variant_title;
    }
    return mapped;
  }

  var _fetch = window.fetch;

  window.fetch = function (url, options) {
    var method = (options && options.method) ? options.method.toUpperCase() : 'GET';
    var path = _urlPath(url);

    // ── add_to_cart ──────────────────────────────────────────────────────────
    if (path === '/cart/add.js' && method === 'POST') {
      return _fetch.apply(this, arguments).then(function (response) {
        if (response.ok) {
          response.clone().json().then(function (data) {
            // Shopify cart/add.js risponde in due forme:
            //  - Modern Section Rendering: { items: [...], sections: {...} }  ← Eurus PDP + quick-add
            //  - Legacy single-item:        { id, product_id, variant_id, ... } ← gift wrap toggle
            // Per il caso modern, items[0] è il PRIMARY product (engraving/insurance secondari
            // vengono pushati DOPO dal codice tema), quindi firiamo add_to_cart solo per il primo.
            var raw = (data && data.items) ? data.items[0] : data;
            if (!raw || !raw.id) return;
            var item = _mapItem(raw);
            if (!item.item_id || item.item_id === 'undefined') return;
            _safeTrack({
              event: 'add_to_cart',
              ecommerce: {
                currency: _getCurrency(),
                value: item.price * item.quantity,
                items: [item]
              }
            });
          }).catch(function () {});
        }
        return response;
      });
    }

    // ── view_cart + add_to_cart diff fallback (fired by xCartAnalytics.viewCart) ──
    if (path === '/cart.js' && method === 'GET') {
      return _fetch.apply(this, arguments).then(function (response) {
        if (response.ok) {
          response.clone().json().then(function (cart) {
            // Snapshot previous cache BEFORE update to detect add_to_cart we missed
            // (Alpine.js / theme code may call /cart/add.js outside our window.fetch
            // wrapper if it cached the original fetch reference before our patch.)
            var oldByVariant = {};
            (window.__rainbowCartItems || []).forEach(function (i) {
              if (i && i.variant_id) oldByVariant[i.variant_id] = i.quantity;
            });
            var hadOldCache = (window.__rainbowCartItems || []).length > 0;

            _updateCache(cart);
            if (!cart.items || cart.items.length === 0) return;

            // Diff: any variant whose qty went up since last cache → synth add_to_cart
            if (hadOldCache) {
              cart.items.forEach(function (newItem) {
                var oldQty = oldByVariant[newItem.variant_id] || 0;
                var diff = newItem.quantity - oldQty;
                if (diff > 0) {
                  var item = _mapItem(newItem, diff);
                  if (!item.item_id || item.item_id === 'undefined') return;
                  _safeTrack({
                    event: 'add_to_cart',
                    ecommerce: {
                      currency: cart.currency || _getCurrency(),
                      value: item.price * diff,
                      items: [item]
                    }
                  });
                }
              });
            }

            // Debounce: skip if fired less than 3 seconds ago (x-intersect.full
            // triggers multiple times during the cart drawer open animation)
            var now = Date.now();
            if (now - _lastViewCart < 3000) return;
            _lastViewCart = now;
            var items = cart.items.slice(0, 20).map(function (item) { return _mapItem(item); });
            _safeTrack({
              event: 'view_cart',
              ecommerce: {
                currency: cart.currency || _getCurrency(),
                value: parseFloat(cart.total_price) / 100,
                items: items
              }
            });
          }).catch(function () {});
        }
        return response;
      });
    }

    // ── remove_from_cart (cart/change.js: remove or quantity decrease) ───────
    if (path === '/cart/change.js' && method === 'POST') {
      var requestBody = null;
      try { requestBody = JSON.parse(options && options.body ? options.body : '{}'); } catch (e) {}
      // Snapshot old items BEFORE the request resolves
      var oldItems = window.__rainbowCartItems.slice();

      return _fetch.apply(this, arguments).then(function (response) {
        if (response.ok && requestBody) {
          response.clone().json().then(function (cartData) {
            _updateCache(cartData);

            var newQty = parseInt(requestBody.quantity, 10);
            var line = parseInt(requestBody.line, 10); // 1-indexed
            if (isNaN(newQty) || isNaN(line)) return;

            var oldItem = oldItems[line - 1];
            if (!oldItem) return;

            var removedQty = oldItem.quantity - newQty;
            if (removedQty <= 0) return; // quantity increased — not a removal

            var item = _mapItem(oldItem, removedQty);
            _safeTrack({
              event: 'remove_from_cart',
              ecommerce: {
                currency: _getCurrency(),
                value: item.price * removedQty,
                items: [item]
              }
            });
          }).catch(function () {});
        }
        return response;
      });
    }

    return _fetch.apply(this, arguments);
  };

  // ─── XHR fallback: some legacy Shopify themes use XMLHttpRequest ─────────
  // Hook XMLHttpRequest.open + send so we catch /cart/add.js submissions that
  // don't go through window.fetch (Eurus drawer, jQuery $.ajax, etc.)
  var _XhrOpen = XMLHttpRequest.prototype.open;
  var _XhrSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.open = function (method, url) {
    this.__rb_url = url;
    this.__rb_method = String(method || 'GET').toUpperCase();
    return _XhrOpen.apply(this, arguments);
  };
  XMLHttpRequest.prototype.send = function () {
    var xhr = this;
    if (xhr.__rb_method === 'POST' && _urlPath(xhr.__rb_url || '') === '/cart/add.js') {
      xhr.addEventListener('load', function () {
        try {
          if (xhr.status >= 200 && xhr.status < 300) {
            var data = JSON.parse(xhr.responseText);
            if (data.items) return; // bulk-add
            var item = _mapItem(data);
            if (!item.item_id || item.item_id === 'undefined') return;
            _safeTrack({
              event: 'add_to_cart',
              ecommerce: { currency: _getCurrency(), value: item.price * item.quantity, items: [item] },
            });
          }
        } catch (e) { /* swallow */ }
      });
    }
    return _XhrSend.apply(this, arguments);
  };

  // ─── Form-submit fallback: traditional <form action="/cart/add"> ─────────
  // Capture-phase listener so we fire even if the form navigates synchronously.
  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form || form.tagName !== 'FORM') return;
    var action = form.getAttribute('action') || '';
    if (!/\/cart\/add/.test(action)) return;
    // Best-effort item info — read hidden inputs
    var qty = parseInt(form.querySelector('[name="quantity"]')?.value || '1', 10) || 1;
    var variantId = form.querySelector('[name="id"]')?.value || null;
    if (!variantId) return;
    _safeTrack({
      event: 'add_to_cart',
      ecommerce: {
        currency: _getCurrency(),
        items: [{ item_id: String(variantId), quantity: qty }],
      },
    });
  }, true);
})();
